import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { useCartStore, type CartMode } from '@/stores/cart.store';

const MODE_LABEL: Record<CartMode, string> = {
  regular: 'penjualan reguler',
  foc: 'tiket FOC',
};

/** Where each flow's catalog lives — "Batal" returns the cashier there. */
const MODE_HOME: Record<CartMode, string> = {
  regular: '/',
  foc: '/foc',
};

/**
 * Keeps a cart in ONE flow. A catalog page declares its mode: on an empty cart
 * the cart simply adopts it; on a cart already holding the other flow's
 * tickets the cashier is asked to clear it (or is sent back to that flow).
 *
 * Returns `mismatch` so the page can refuse adds (e.g. a barcode scan) while
 * the question is open.
 */
export function useCartModeGuard(mode: CartMode) {
  const navigate = useNavigate();
  const cartMode = useCartStore((s) => s.mode);
  const lineCount = useCartStore((s) => s.lines.length + s.bundleLines.length);
  const setMode = useCartStore((s) => s.setMode);
  const clearCart = useCartStore((s) => s.clearCart);

  // An empty cart (including one just cleared — `clearCart` resets to
  // 'regular') takes this page's mode.
  useEffect(() => {
    if (lineCount === 0 && cartMode !== mode) setMode(mode);
  }, [lineCount, cartMode, mode, setMode]);

  const mismatch = lineCount > 0 && cartMode !== mode;

  const keepCart = () => navigate(MODE_HOME[cartMode], { replace: true });
  const switchFlow = () => {
    clearCart();
    setMode(mode);
  };

  const dialog = (
    // Controlled by `mismatch` alone: Radix also reports a close for the
    // Action, so an onOpenChange → keepCart would navigate away after clearing.
    // Escape is routed explicitly instead.
    <AlertDialog open={mismatch}>
      <AlertDialogContent onEscapeKeyDown={keepCart}>
        <AlertDialogHeader>
          <AlertDialogTitle>Kosongkan keranjang?</AlertDialogTitle>
          <AlertDialogDescription>
            Keranjang masih berisi {MODE_LABEL[cartMode]}. Tiket FOC dan tiket reguler tidak dapat
            digabung dalam satu transaksi — kosongkan keranjang untuk memulai {MODE_LABEL[mode]}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={keepCart}>Batal</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: 'destructive' })}
            onClick={switchFlow}
          >
            Kosongkan &amp; lanjut
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { mismatch, dialog };
}
